// src/context/AuthContext.tsx
import React from 'react';
import { supabase } from '@/integrations/supabase/client';

type Profile = { id: string; role: 'admin' | 'jogador'; jogador_id: number | null };
type AuthContextType = {
  loading: boolean;
  session: any | null;
  profile: Profile | null;
  jogadorId: number | null;
};

const AuthContext = React.createContext<AuthContextType>({
  loading: true,
  session: null,
  profile: null,
  jogadorId: null,
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  
  const [loading, setLoading] = React.useState(true);
  const [session, setSession] = React.useState<any | null>(null);
  const [profile, setProfile] = React.useState<Profile | null>(null);
  const fetchingProfileRef = React.useRef<string | null>(null);
  const initialLoadDone = React.useRef(false);

  const fetchProfile = React.useCallback(async (userId: string) => {
    // Evita chamadas duplicadas simultâneas
    if (fetchingProfileRef.current === userId) return;
    
    fetchingProfileRef.current = userId;
    
    try {
      // Timeout de 5 segundos para evitar travamento
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Timeout ao buscar profile')), 5000);
      });
      
      const fetchPromise = supabase
        .from('profiles')
        .select('id, role, jogador_id')
        .eq('id', userId)
        .maybeSingle();
      
      const result = await Promise.race([fetchPromise, timeoutPromise]) as any;
      
      const { data, error } = result;
      
      if (error) {
        console.error('Erro ao buscar profile:', error.message);
        setProfile(null);
      } else if (data) {
        setProfile({ id: String(data.id), role: data.role as 'admin' | 'jogador', jogador_id: (data.jogador_id ?? null) as number | null });
      } else {
        // Perfil não existe - tenta criar e vincular com jogador
        try {
          // Busca o email do usuário
          const { data: { user } } = await supabase.auth.getUser();
          const userEmail = user?.email;
          
          let jogadorId: number | null = null;
          
          // Se tiver email, busca jogador correspondente
          if (userEmail) {
            const { data: jogador, error: jogadorError } = await supabase
              .from('jogadores')
              .select('id')
              .eq('email', userEmail)
              .maybeSingle();
            
            if (!jogadorError && jogador) {
              jogadorId = jogador.id;
              
              // Atualiza o user_id na tabela jogadores
              await supabase
                .from('jogadores')
                .update({ user_id: userId })
                .eq('id', jogador.id);
            }
          }
          
          // Cria o perfil com ou sem jogador_id
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              role: 'jogador',
              jogador_id: jogadorId,
              is_active: true
            })
            .select('id, role, jogador_id')
            .single();
          
          if (insertError) {
            console.error('Erro ao criar perfil:', insertError.message);
            setProfile(null);
          } else if (newProfile) {
            setProfile({ 
              id: String(newProfile.id), 
              role: newProfile.role as 'admin' | 'jogador', 
              jogador_id: (newProfile.jogador_id ?? null) as number | null 
            });
          } else {
            setProfile(null);
          }
        } catch (createError) {
          console.error('Erro ao criar perfil:', createError);
          setProfile(null);
        }
      }
    } catch (error) {
      // Timeout ou outro erro - continua sem profile
      console.error('Erro ao processar profile:', error);
      setProfile(null);
    } finally {
      fetchingProfileRef.current = null;
      setLoading(false);
    }
  }, []);

  // Carrega sessão inicial
  React.useEffect(() => {
    let mounted = true;
    
    (async () => {
      try {
        // Tenta obter sessão do storage
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        
        let sess = data.session ?? null;
        
        // Se não encontrou, tenta refresh
        if (!sess) {
          try {
            const { data: refreshed } = await supabase.auth.refreshSession();
            sess = refreshed?.session ?? null;
          } catch {}
        }
        
        setSession(sess);
        
        if (sess?.user?.id) {
          await fetchProfile(sess.user.id);
        }
      } catch (error) {
        console.error('Erro ao carregar sessão:', error);
        setSession(null);
        setProfile(null);
      } finally {
        if (mounted) {
          setLoading(false);
          initialLoadDone.current = true;
        }
      }
    })();
    
    return () => { mounted = false; };
  }, [fetchProfile]);

  // Listener de mudanças de autenticação
  React.useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, sess) => {
      // Ignora eventos durante o carregamento inicial
      if (!initialLoadDone.current) {
        return;
      }
      setSession(sess);
      
      if (sess?.user?.id) {
        fetchProfile(sess.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Opcional: ouvir mudanças de role em tempo real
  React.useEffect(() => {
    // Só conecta se houver sessão E após loading inicial
    if (!session?.user?.id || loading) return;
    
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let isSubscribed = false;
    
    try {
      const userId = session.user.id;
      channel = supabase
        .channel(`profiles-role-${userId}`) // Nome único por usuário
        .on(
          'postgres_changes',
          { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
          () => {
            if (isSubscribed) fetchProfile(userId);
          }
        )
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            isSubscribed = true;
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            isSubscribed = false;
          }
        });
    } catch {
      // Realtime é opcional
    }
    
    return () => {
      isSubscribed = false;
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch {}
      }
    };
  }, [session, loading, fetchProfile]);

  
  return (
    <AuthContext.Provider
      value={{
        loading,
        session,
        profile,
        jogadorId: profile?.jogador_id ?? null,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthProfile = () => React.useContext(AuthContext);